import {
  AttachFileOutlined,
  Close,
  FileUploadOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  type ButtonProps,
  Button as MuiButton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import type React from "react";
import { forwardRef, useImperativeHandle, useRef } from "react";

interface CustomFile {
  name: string;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { UploadFileProps } from '@monthly/ui' */
export interface UploadFileProps {
  currentFile: CustomFile | null;
  id: string;
  label: string;
  disabled?: boolean;
  error?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

const Button = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "filled" && prop !== "error",
})<ButtonProps & { filled: boolean; error?: boolean }>(
  ({ theme, disabled, filled, error }) => ({
    height: "56px",
    width: "280px",
    border: "1.5px dashed",
    backgroundColor: filled ? theme.palette.action.selected : "default",
    borderColor: error
      ? theme.palette.error.main
      : filled
        ? theme.palette.secondary[200]
        : theme.palette.secondary[100],

    "&.Mui-disabled": {
      color: theme.palette.text.disabled,
      borderColor: error
        ? theme.palette.error.main
        : theme.palette.secondary[100],
      border: "1.5px dashed",
      svg: {
        color: theme.palette.action.focus,
      },
      ".MuiTypography-root": {
        color: theme.palette.text.disabled,
      },
    },
    // TODO: check alpha colors with UX
    ":hover": {
      backgroundColor: disabled
        ? "default"
        : filled
          ? theme.palette.action.selected
          : alpha(theme.palette.secondary[50], 0.04),
      borderColor: error
        ? theme.palette.error.main
        : disabled
          ? theme.palette.secondary[100]
          : theme.palette.secondary[200],
    },
  }),
);

const CloseIconButton = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  marginLeft: "10px",
  color: theme.palette.primary.main,
  transition: theme.transitions.create("color", {
    duration: theme.transitions.duration.shortest,
  }),
  "&:hover": {
    color: theme.palette.primary.dark,
  },
  "&:active": {
    color: theme.palette.primary.light,
  },
  "& svg": {
    width: 24,
    height: 24,
  },
}));

const formatFileName = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");

  if (lastDotIndex > 0) {
    const baseName = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);
    const maxBaseLength = Math.max(1, 20 - extension.length);

    const truncatedBase =
      baseName.length > maxBaseLength
        ? baseName.substring(0, maxBaseLength) + "..."
        : baseName;

    return `[${truncatedBase}${extension}]`;
  }

  return `[${fileName}]`;
};

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { UploadFile } from '@monthly/ui' */
export const UploadFile = forwardRef<HTMLInputElement, UploadFileProps>(
  (
    {
      onChange,
      currentFile,
      onClear,
      id,
      disabled = false,
      error = false,
      label,
    },
    ref,
  ) => {
    const internalInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => internalInputRef.current!, [
      internalInputRef,
    ]);

    const triggerFileSelect = () => {
      if (!disabled && internalInputRef.current) {
        internalInputRef.current.click();
      }
    };

    if (currentFile) {
      return (
        <Button
          disableRipple
          variant="outlined"
          filled={currentFile !== null}
          error={error}
          sx={{
            pointerEvents: currentFile.name ? "none" : "auto",
          }}
        >
          <Stack direction="row" gap={1}>
            <AttachFileOutlined
              fontSize="small"
              sx={(theme) => ({
                color: theme.palette.action.focus,
              })}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={(theme) => ({
                fontWeight: theme.typography.fontWeightMedium,
                maxWidth: 200,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "clip",
                whiteSpace: "nowrap",
                display: "block",
              })}
              title={`[${currentFile.name}]`}
            >
              {formatFileName(currentFile.name)}
            </Typography>
          </Stack>

          {!disabled && (
            <CloseIconButton
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClear();
                }
              }}
              sx={{
                pointerEvents: "auto",
              }}
            >
              <Close fontSize="small" />
            </CloseIconButton>
          )}
        </Button>
      );
    }

    return (
      <Box>
        <input
          accept="image/*, application/pdf"
          style={{ display: "none" }}
          id={id}
          type="file"
          onChange={onChange}
          ref={internalInputRef}
        />

        <Button
          fullWidth
          disableRipple
          variant="outlined"
          disabled={disabled}
          component="span"
          onClick={triggerFileSelect}
          filled={currentFile !== null}
          error={error}
          startIcon={
            <FileUploadOutlined
              sx={(theme) => ({
                color: theme.palette.primary.main,
                width: 24,
                height: 24,
              })}
            />
          }
        >
          <Typography
            variant="body2"
            color="text.primary"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightMedium,
            })}
          >
            {label || "Subir archivo"}
          </Typography>
        </Button>
      </Box>
    );
  },
);
